import {
  TbApi,
  TbArrowBackUp,
  TbBook,
  TbBrain,
  TbBrandApple,
  TbBrandWindows,
  TbBulb,
  TbChecklist,
  TbChevronDown,
  TbClock,
  TbDeviceMobile,
  TbDevices,
  TbDownload,
  TbEyeOff,
  TbFileSearch,
  TbFileText,
  TbFilter,
  TbFolder,
  TbInfoCircle,
  TbKey,
  TbLanguage,
  TbListDetails,
  TbLock,
  TbMail,
  TbMessageQuestion,
  TbMessages,
  TbMicrophone,
  TbNotes,
  TbPencilPlus,
  TbPlayerStop,
  TbPlugConnected,
  TbReload,
  TbRobotOff,
  TbRoute,
  TbSearch,
  TbServer,
  TbShieldCheck,
  TbSparkles,
  TbTags,
  TbTerminal2,
  TbTicket,
  TbToggleRight,
  TbUpload,
  TbUserEdit,
  TbUserShield,
  TbUsersGroup,
  TbVideo,
  TbVectorTriangle,
} from "solid-icons/tb";
import { createSignal, For, type ParentComponent, Show } from "solid-js";
import {
  AGENTS,
  BUILT_IN,
  type Logo,
  MCP_SERVERS,
  MEETING_APPS,
  MODEL_PROVIDERS,
} from "~/components/marketing/content";
import {
  actionsDemo,
  followThroughDemo,
  insightsDemo,
  libraryDemo,
  offerFlowDemo,
  smartTagsDemo,
  speakersDemo,
} from "~/components/marketing/demo";
import { IntegrationHub } from "~/components/marketing/IntegrationHub";
import { LogoMark } from "~/components/marketing/LogoMark";
import {
  ActionsMockup,
  FollowThroughMockup,
  InsightsMockup,
  LibraryMockup,
  OfferFlowMockup,
  SmartTagsMockup,
  SpeakersMockup,
} from "~/components/marketing/mockups";
import { MockPanel } from "~/components/marketing/ui";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import { FeatureGroup } from "./FeatureGroup";
import { TrustVisual } from "./TrustVisual";

const pick = (logos: Logo[], names: string[]) => logos.filter((logo) => names.includes(logo.name));

// The MCP catalog by what a team uses each server for. A server added to the catalog
// shows only once it's named in a category here.
const MCP_CATEGORIES: { label: TranslationKey; names: string[] }[] = [
  {
    label: "marketing.features.integrations.cat_sales",
    names: [
      "HubSpot",
      "Salesforce",
      "Intercom",
      "Apollo.io",
      "Klaviyo",
      "Attio",
      "Close",
      "Clay",
      "SurveyMonkey",
      "Tally",
    ],
  },
  {
    label: "marketing.features.integrations.cat_communication",
    names: ["Zoom", "Calendly", "Superhuman", "Front", "Cal.com", "Fireflies", "Granola", "Zight"],
  },
  {
    label: "marketing.features.integrations.cat_projects",
    names: ["Jira", "Asana", "monday.com", "Miro", "ClickUp", "Airtable", "Atlassian", "Lucid", "Todoist"],
  },
  {
    label: "marketing.features.integrations.cat_design",
    names: ["Figma", "Canva", "WordPress", "Webflow", "Wix", "Gamma", "Contentful", "Sanity", "Mintlify"],
  },
  {
    label: "marketing.features.integrations.cat_finance",
    names: ["Stripe", "PayPal", "Shopify", "QuickBooks", "Ramp", "Brex", "Mercury", "Gusto"],
  },
  {
    label: "marketing.features.integrations.cat_engineering",
    names: [
      "Vercel",
      "Cloudflare",
      "Supabase",
      "Postman",
      "Netlify",
      "Lovable",
      "Render",
      "Neon",
      "PlanetScale",
      "Prisma",
      "Clerk",
      "WorkOS",
      "Stytch",
    ],
  },
  {
    label: "marketing.features.integrations.cat_monitoring",
    names: [
      "Sentry",
      "PagerDuty",
      "Amplitude",
      "Mixpanel",
      "New Relic",
      "PostHog",
      "Ahrefs",
      "Honeycomb",
      "Axiom",
      "incident.io",
    ],
  },
  {
    label: "marketing.features.integrations.cat_ai",
    names: ["Hugging Face", "Exa", "Firecrawl", "Tavily", "Browserbase", "Apify", "Context7"],
  },
  { label: "marketing.features.integrations.cat_files", names: ["Dropbox", "Box", "Zapier", "Make"] },
];

const AppleMark = (props: { class?: string }) => <TbBrandApple class={`text-platform-apple ${props.class ?? ""}`} />;
const WindowsMark = (props: { class?: string }) => (
  <TbBrandWindows class={`text-platform-windows ${props.class ?? ""}`} />
);
const IphoneMark = (props: { class?: string }) => (
  <TbDeviceMobile class={`text-platform-mobile ${props.class ?? ""}`} />
);

const PLATFORMS: Logo[] = [
  { name: "Mac", mark: { kind: "svg", icon: AppleMark, color: "#6b7280" } },
  { name: "Windows", mark: { kind: "svg", icon: WindowsMark, color: "#0078D4" } },
  { name: "iPhone", mark: { kind: "svg", icon: IphoneMark, color: "#34C759" } },
];

// The ten transcription languages, as their ISO codes.
const LANGUAGES = ["EN", "ES", "FR", "DE", "HI", "IT", "JA", "NL", "PT", "RU"];

const Stage: ParentComponent = (props) => (
  <MockPanel>
    <div class="mx-auto max-w-5xl">{props.children}</div>
  </MockPanel>
);

export const LiveSessionGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="live-session"
      index="01"
      eyebrow={t("marketing.features.live.eyebrow")}
      title={t("marketing.features.live.title")}
      body={t("marketing.features.live.body")}
      visual={
        <Stage>
          <OfferFlowMockup data={offerFlowDemo(t)} />
        </Stage>
      }
      details={[
        {
          icon: TbVideo,
          color: "#0b5cff",
          title: "marketing.features.live.apps_title",
          body: "marketing.features.live.apps_body",
          href: "/download#download-steps",
          logos: MEETING_APPS,
          wide: true,
        },
        {
          icon: TbRobotOff,
          color: "#1d9e75",
          title: "marketing.features.live.no_bot_title",
          body: "marketing.features.live.no_bot_body",
          href: "/compare",
        },
        {
          icon: TbLanguage,
          color: "#7c3aed",
          title: "marketing.features.live.languages_title",
          body: "marketing.features.live.languages_body",
          href: "#speakers",
          chips: LANGUAGES,
        },
        {
          icon: TbNotes,
          color: "#d97706",
          title: "marketing.features.live.notes_title",
          body: "marketing.features.live.notes_body",
          href: "#auto-apply-tags",
        },
        {
          icon: TbMessageQuestion,
          color: "#0891b2",
          title: "marketing.features.live.ask_title",
          body: "marketing.features.live.ask_body",
          href: "#memory",
        },
        {
          icon: TbSparkles,
          color: "#c2410c",
          title: "marketing.features.live.offers_title",
          body: "marketing.features.live.offers_body",
          href: "/for-executives#execs-offers",
        },
        {
          icon: TbVectorTriangle,
          color: "#db2777",
          title: "marketing.features.live.canvas_title",
          body: "marketing.features.live.canvas_body",
          href: "/for-executives#execs-together",
        },
        {
          icon: TbDevices,
          color: "#475569",
          title: "marketing.features.live.devices_title",
          body: "marketing.features.live.devices_body",
          href: "/download#platforms",
          logos: PLATFORMS,
        },
      ]}
    />
  );
};

export const AutoTagsGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="auto-apply-tags"
      index="03"
      eyebrow={t("marketing.features.tags.eyebrow")}
      title={t("marketing.features.tags.title")}
      body={t("marketing.features.tags.body")}
      visual={
        <Stage>
          <SmartTagsMockup data={smartTagsDemo(t)} />
        </Stage>
      }
      details={[
        {
          icon: TbTags,
          color: "#d97706",
          title: "marketing.features.tags.defaults_title",
          body: "marketing.features.tags.defaults_body",
        },
        {
          icon: TbPencilPlus,
          color: "#3049cf",
          title: "marketing.features.tags.own_title",
          body: "marketing.features.tags.own_body",
        },
        {
          icon: TbArrowBackUp,
          color: "#1d9e75",
          title: "marketing.features.tags.correct_title",
          body: "marketing.features.tags.correct_body",
        },
        {
          icon: TbFilter,
          color: "#7c3aed",
          title: "marketing.features.tags.filter_title",
          body: "marketing.features.tags.filter_body",
        },
      ]}
    />
  );
};

export const ProactiveGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="proactive-intelligence"
      index="02"
      eyebrow={t("marketing.features.proactive.eyebrow")}
      title={t("marketing.features.proactive.title")}
      body={t("marketing.features.proactive.body")}
      visual={
        <Stage>
          <InsightsMockup data={insightsDemo(t)} />
        </Stage>
      }
      details={[
        {
          icon: TbBulb,
          color: "#c2410c",
          title: "marketing.features.proactive.answer_title",
          body: "marketing.features.proactive.answer_body",
        },
        {
          icon: TbFileSearch,
          color: "#0891b2",
          title: "marketing.features.proactive.found_title",
          body: "marketing.features.proactive.found_body",
        },
        {
          icon: TbInfoCircle,
          color: "#475569",
          title: "marketing.features.proactive.context_title",
          body: "marketing.features.proactive.context_body",
        },
        {
          icon: TbRoute,
          color: "#db2777",
          title: "marketing.features.proactive.next_title",
          body: "marketing.features.proactive.next_body",
        },
      ]}
    />
  );
};

export const SpeakersGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="speakers"
      index="04"
      eyebrow={t("marketing.features.speakers.eyebrow")}
      title={t("marketing.features.speakers.title")}
      body={t("marketing.features.speakers.body")}
      visual={
        <Stage>
          <SpeakersMockup data={speakersDemo(t)} />
        </Stage>
      }
      details={[
        {
          icon: TbUserEdit,
          color: "#3049cf",
          title: "marketing.features.speakers.name_title",
          body: "marketing.features.speakers.name_body",
        },
        {
          icon: TbUsersGroup,
          color: "#1d9e75",
          title: "marketing.features.speakers.remember_title",
          body: "marketing.features.speakers.remember_body",
        },
        {
          icon: TbMicrophone,
          color: "#d97706",
          title: "marketing.features.speakers.self_title",
          body: "marketing.features.speakers.self_body",
        },
        {
          icon: TbMessages,
          color: "#7c3aed",
          title: "marketing.features.speakers.ask_title",
          body: "marketing.features.speakers.ask_body",
          href: "/changelog/chat-with-live-sessions",
        },
      ]}
    />
  );
};

export const AfterMeetingGroup = () => {
  const { t } = useI18n();
  // Bumped by Replay so the keyed mockup below remounts and plays from its first beat.
  const [run, setRun] = createSignal(0);
  return (
    <FeatureGroup
      id="after-the-meeting"
      index="05"
      eyebrow={t("marketing.features.after.eyebrow")}
      title={t("marketing.features.after.title")}
      body={t("marketing.features.after.body")}
      visual={
        <Stage>
          <Show when={run() + 1} keyed>
            <FollowThroughMockup data={followThroughDemo(t)} />
          </Show>
          <button
            type="button"
            onClick={() => setRun(run() + 1)}
            class="ms-auto mt-3 flex w-fit cursor-pointer items-center gap-1.5 rounded-full border border-primary-border bg-tile px-3 py-1.5 text-sm font-medium text-secondary-text shadow-sm transition-colors hover:text-primary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none motion-reduce:hidden"
          >
            <TbReload class="size-4" aria-hidden="true" />
            {t("marketing.mock.replay")}
          </button>
        </Stage>
      }
      details={[
        {
          icon: TbFileText,
          color: "#3049cf",
          title: "marketing.features.after.docs_title",
          body: "marketing.features.after.docs_body",
          href: "/blog/spec-driven-development-when-it-pays",
        },
        {
          icon: TbTerminal2,
          color: "#d97757",
          title: "marketing.features.after.agents_title",
          body: "marketing.features.after.agents_body",
          href: "/for-builders#builders-steps",
          logos: pick(AGENTS, ["Claude Code", "Codex", "Cursor"]),
          wide: true,
        },
        {
          icon: TbMail,
          color: "#ea4335",
          title: "marketing.features.after.send_title",
          body: "marketing.features.after.send_body",
          href: "/for-executives#execs-followup",
          logos: pick(BUILT_IN, ["Gmail", "Slack", "Google Calendar", "Notion"]),
          wide: true,
        },
        {
          icon: TbTicket,
          color: "#5e6ad2",
          title: "marketing.features.after.linear_title",
          body: "marketing.features.after.linear_body",
          href: "/for-builders#builders-issues",
          logos: pick(BUILT_IN, ["Linear"]),
        },
        {
          icon: TbDownload,
          color: "#0d9488",
          title: "marketing.features.after.export_title",
          body: "marketing.features.after.export_body",
          href: "https://docs.chief.bot/help/meetings-live-sessions/copying-downloading-sharing-transcripts",
        },
        {
          icon: TbUpload,
          color: "#0891b2",
          title: "marketing.features.after.upload_title",
          body: "marketing.features.after.upload_body",
          href: "/changelog/live-session-app-v1-1-4",
        },
        {
          icon: TbMessages,
          color: "#7c3aed",
          title: "marketing.features.after.chat_title",
          body: "marketing.features.after.chat_body",
          href: "/changelog/chat-with-live-sessions",
        },
      ]}
    />
  );
};

export const IntegrationsGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="integrations"
      index="06"
      eyebrow={t("marketing.features.integrations.eyebrow")}
      title={t("marketing.features.integrations.title")}
      body={t("marketing.features.integrations.body")}
      visual={<IntegrationHub labelledBy="integrations-title" />}
      details={[
        {
          icon: TbPlugConnected,
          color: "#1d9e75",
          title: "marketing.features.integrations.builtin_title",
          body: "marketing.features.integrations.builtin_body",
          href: "https://docs.chief.bot/help/integrations/integrations-overview",
          logos: BUILT_IN,
          wide: true,
        },
        {
          icon: TbServer,
          color: "#3049cf",
          title: "marketing.features.integrations.server_title",
          body: "marketing.features.integrations.server_body",
          href: "/for-builders#builders-mcp",
          logos: pick(AGENTS, ["Claude Code", "Cursor", "Codex"]),
        },
        {
          icon: TbApi,
          color: "#0891b2",
          title: "marketing.features.integrations.api_title",
          body: "marketing.features.integrations.api_body",
          href: "/for-builders#builders-api",
        },
        {
          icon: TbKey,
          color: "#d97706",
          title: "marketing.features.integrations.byok_title",
          body: "marketing.features.integrations.byok_body",
          href: "/changelog/byok-provider-keys",
          logos: MODEL_PROVIDERS,
          wide: true,
        },
      ]}
    >
      <div class="mt-14 border-t border-primary-border pt-10">
        <h3 class="font-mono text-xs font-semibold tracking-[0.18em] text-muted-text uppercase">
          {t("marketing.home.integrations.group_mcp")}
        </h3>
        <p class="mt-3 max-w-2xl text-secondary-text">{t("marketing.features.integrations.catalog_body")}</p>
        <div class="mt-6 grid items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
          <For each={MCP_CATEGORIES}>{(category) => <McpCategory {...category} />}</For>
        </div>
      </div>
    </FeatureGroup>
  );
};

// Two rows per category until the visitor asks for the rest.
const MCP_PREVIEW = 4;

const McpCategory = (props: { label: TranslationKey; names: string[] }) => {
  const { t } = useI18n();
  const [open, setOpen] = createSignal(false);
  // In the category's order rather than the catalog's: only the first few show before "more".
  const logos = () => props.names.flatMap((name) => MCP_SERVERS.filter((logo) => logo.name === name));
  const hidden = () => logos().length - MCP_PREVIEW;
  return (
    <section class="rounded-2xl border border-primary-border bg-tile p-4">
      <h4 class="flex items-baseline justify-between px-1 text-sm font-semibold">
        {t(props.label)}
        <span class="font-mono text-xs font-normal text-muted-text">{logos().length}</span>
      </h4>
      <ul aria-label={t(props.label)} class="mt-3 grid grid-cols-2 gap-1">
        <For each={open() ? logos() : logos().slice(0, MCP_PREVIEW)}>
          {(logo) => (
            <li class="flex min-w-0 items-center gap-2.5 rounded-lg p-1.5 transition-colors hover:bg-panel">
              <span class="grid size-8 shrink-0 place-items-center rounded-lg border border-primary-border bg-canvas">
                <LogoMark mark={logo.mark} class="size-4.5" />
              </span>
              <span class="truncate text-sm">{logo.name}</span>
            </li>
          )}
        </For>
      </ul>
      <Show when={hidden() > 0}>
        <button
          type="button"
          aria-expanded={open()}
          onClick={() => setOpen(!open())}
          class="mt-2 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold text-brand-text transition-colors hover:bg-panel focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          <Show
            when={open()}
            fallback={
              <>
                <span class="flex -space-x-1.5">
                  <For each={logos().slice(MCP_PREVIEW, MCP_PREVIEW + 3)}>
                    {(logo) => (
                      <span class="grid size-5 place-items-center rounded-full border border-primary-border bg-canvas">
                        <LogoMark mark={logo.mark} class="size-3" />
                      </span>
                    )}
                  </For>
                </span>
                {t("marketing.features.integrations.show_more", { count: String(hidden()) })}
              </>
            }
          >
            {t("marketing.features.integrations.show_less")}
          </Show>
          <TbChevronDown
            class="size-3.5 transition-transform"
            classList={{ "rotate-180": open() }}
            aria-hidden="true"
          />
        </button>
      </Show>
    </section>
  );
};

export const MemoryGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="memory"
      index="07"
      eyebrow={t("marketing.features.memory.eyebrow")}
      title={t("marketing.features.memory.title")}
      body={t("marketing.features.memory.body")}
      visual={
        <Stage>
          <LibraryMockup data={libraryDemo(t)} />
        </Stage>
      }
      details={[
        {
          icon: TbFolder,
          color: "#3049cf",
          title: "marketing.features.memory.library_title",
          body: "marketing.features.memory.library_body",
          href: "https://docs.chief.bot/help/your-knowledge-library/what-is-the-library",
        },
        {
          icon: TbSparkles,
          color: "#d97706",
          title: "marketing.features.memory.auto_title",
          body: "marketing.features.memory.auto_body",
          href: "https://docs.chief.bot/help/meetings-live-sessions/finding-live-session-recordings-in-the-web-app",
        },
        {
          icon: TbBrain,
          color: "#db2777",
          title: "marketing.features.memory.memories_title",
          body: "marketing.features.memory.memories_body",
          href: "https://docs.chief.bot/help/memories/what-are-memories",
        },
        {
          icon: TbSearch,
          color: "#0891b2",
          title: "marketing.features.memory.search_title",
          body: "marketing.features.memory.search_body",
          href: "https://docs.chief.bot/help/your-knowledge-library/searching-your-library",
        },
      ]}
    />
  );
};

export const AutomationGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="automation"
      index="08"
      eyebrow={t("marketing.features.automation.eyebrow")}
      title={t("marketing.features.automation.title")}
      body={t("marketing.features.automation.body")}
      visual={
        <Stage>
          <ActionsMockup data={actionsDemo(t)} />
        </Stage>
      }
      details={[
        {
          icon: TbClock,
          color: "#3049cf",
          title: "marketing.features.automation.schedule_title",
          body: "marketing.features.automation.schedule_body",
          href: "/changelog/actions-improvements",
        },
        {
          icon: TbPlayerStop,
          color: "#e5484d",
          title: "marketing.features.automation.trigger_title",
          body: "marketing.features.automation.trigger_body",
          href: "/for-executives#execs-autopilot",
        },
        {
          icon: TbToggleRight,
          color: "#1d9e75",
          title: "marketing.features.automation.control_title",
          body: "marketing.features.automation.control_body",
          href: "/for-builders#builders-mcp",
        },
        {
          icon: TbBook,
          color: "#7c3aed",
          title: "marketing.features.automation.skills_title",
          body: "marketing.features.automation.skills_body",
          href: "https://docs.chief.bot/help/skills/what-are-skills",
        },
      ]}
    />
  );
};

export const TrustGroup = () => {
  const { t } = useI18n();
  return (
    <FeatureGroup
      id="trust"
      index="09"
      eyebrow={t("marketing.features.trust.eyebrow")}
      title={t("marketing.features.trust.title")}
      body={t("marketing.features.trust.body")}
      visual={<TrustVisual />}
      details={[
        {
          icon: TbChecklist,
          color: "#3049cf",
          title: "marketing.features.trust.approval_title",
          body: "marketing.features.trust.approval_body",
          href: "/for-executives#execs-offers",
        },
        {
          icon: TbUserShield,
          color: "#7c3aed",
          title: "marketing.features.trust.private_title",
          body: "marketing.features.trust.private_body",
          href: "/changelog/chat-visibility-and-sharing",
        },
        {
          icon: TbListDetails,
          color: "#475569",
          title: "marketing.features.trust.audit_title",
          body: "marketing.features.trust.audit_body",
          href: "/changelog/organization-audit-log",
        },
        {
          icon: TbLock,
          color: "#d97706",
          title: "marketing.features.trust.encryption_title",
          body: "marketing.features.trust.encryption_body",
          href: "/privacy#integrations",
        },
        {
          icon: TbEyeOff,
          color: "#db2777",
          title: "marketing.features.trust.training_title",
          body: "marketing.features.trust.training_body",
          href: "/privacy#responsible-ai",
        },
        {
          icon: TbShieldCheck,
          color: "#0d9488",
          title: "marketing.features.trust.certs_title",
          body: "marketing.features.trust.certs_body",
          href: "https://trust.chief.bot",
        },
      ]}
    />
  );
};
